import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import ReactPlayer from 'react-player';
import { getUpcomingShow } from '../firestore';
import { expiredAfterHours, formatDate } from './date';

const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
`;

const Logo = styled.img.attrs({
  src: require('./logo.png'),
})`
  margin: 30px 0;
  height: 70px;
  display: flex;
`;

const Video = styled(ReactPlayer)`
  display: flex;
`;

const PlayButton = ({
  onClick,
  playing,
}: {
  onClick: () => void;
  playing: boolean;
}) => (
  <PlayButton.Container>
    {!playing && <PlayButton.Icon onClick={onClick} />}
  </PlayButton.Container>
);

PlayButton.Icon = styled.img.attrs({
  src: require('./PlayingButton.svg'),
})`
  display: flex;
  width: 80px;
  height: 80px;
`;

PlayButton.Container = styled.div`
  display: flex;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  justify-content: center;
  align-items: center;
`;

const VideoContainer = styled.div<{ ready: boolean }>`
  display: flex;
  margin: 0 18px;
  height: ${({ ready }) => (ready ? null : 300)}px;
  position: relative;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  background-color: darkslategrey;
  border-top-right-radius: 16px;
  border-top-left-radius: 16px;
  -webkit-mask-image: -webkit-radial-gradient(
    circle,
    white 100%,
    black 100%
  ); /*ios 7 border-radius-bug */
  -webkit-transform: rotate(
    0.000001deg
  ); /*mac os 10.6 safari 5 border-radius-bug */
  overflow: hidden;
`;

const Content = styled.div`
  display: flex;
  background-color: white;
  margin: -5px 18px 34px;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
  padding: 44px 35px;
  align-self: stretch;
  flex-direction: column;
  align-items: center;
`;

const Avatar = styled.img`
  width: 78px;
  height: 78px;
  border-radius: 39px;
  background-color: #eeeeee;
`;

const Description = ({
  username,
  date,
}: {
  username?: string;
  date: string;
}) => (
  <Description.Container>
    <Description.Username>{username} </Description.Username>
    Tune in and watch my show <Description.Date>{date}</Description.Date>
  </Description.Container>
);

const Expired = () => (
  <Description.Container>
    Aw shucks! Looks like you missed this show.
    <br />
    Don't worry though! There's plenty more happening at Popshop, go to the app
    now to discover new exciting shows.
  </Description.Container>
);

Description.Container = styled.div`
  text-align: center;
  margin: 15px 0;
  font-family: 'Montserrat', sans-serif;
  font-size: 18px;
`;

Description.Username = styled.span`
  display: none;
  font-weight: 700;
  color: #4e67f0;
`;

Description.Date = styled.span`
  text-decoration: underline;
`;

const ErrorView = styled.span.attrs({
  children: 'Something is wrong... please try again',
})`
  font-size: 17px;
  text-align: center;
  color: red;
`;

const GetApp = styled.div.attrs({ children: 'Get the app now!' })`
  font-family: 'Montserrat', sans-serif;
  font-size: 20px;
  margin-bottom: 20px;
`;

const AppStore = ({ href }: { href: string }) => (
  <a href={href}>
    <AppStore.Logo />
  </a>
);

const Loading = () => (
  <div className="lds-roller">
    <div />
    <div />
    <div />
    <div />
    <div />
    <div />
    <div />
    <div />
  </div>
);

AppStore.Logo = styled.img.attrs({
  src: require('./appstore.svg'),
})`
  width: 180px;
`;

const UpcomingShowPage = () => {
  const [error, setError] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);
  const [state, setState] = useState<
    | {
        url: string;
        avatar: string;
        date: Date;
      }
    | undefined
  >(undefined);

  useEffect(() => {
    document.body.className = 'yellow-background';

    const showID =
      window.location.pathname && window.location.pathname.slice(14);
    if (!showID) {
      setError(true);
      return;
    }

    getUpcomingShow(showID).then(data => {
      if (!data) {
        setError(true);
      } else {
        setState({
          url: data.previewVideoURL,
          avatar: data.profileImage,
          date: data.startsAt.toDate(),
        });
      }
    });
  }, []);

  return (
    <Container>
      <Logo />
      <VideoContainer ready={ready}>
        {!ready && (error ? <ErrorView /> : <Loading />)}
        {state && (
          <Video
            width={undefined}
            height={undefined}
            controls={false}
            loop={true}
            style={{ display: ready ? 'flex' : 'none' }}
            onReady={() => setReady(true)}
            playing={playing}
            onPause={() => setPlaying(false)}
            url={state.url}
          />
        )}
        {ready && (
          <PlayButton playing={playing} onClick={() => setPlaying(true)} />
        )}
      </VideoContainer>
      <Content>
        {state && state.avatar && <Avatar src={state.avatar} />}
        {state &&
          (expiredAfterHours(state.date) ? (
            <Expired />
          ) : (
            <Description date={formatDate(state.date)} />
          ))}
        <GetApp />
        <AppStore href="https://itunes.apple.com/us/app/popshop-start-shopping-us/id1009480270?ls=1&mt=8" />
      </Content>
    </Container>
  );
};

export default UpcomingShowPage;
